import { useEffect, useRef, useState } from 'react';
import { MDBAlert, MDBInput,MDBCheckbox, MDBBtn, MDBRow, MDBCol, MDBTypography, MDBInputGroup, MDBCard, MDBCardBody, MDBCardTitle, MDBFile, MDBIcon, MDBTabs, MDBTabsItem, MDBTabsLink, MDBTabsContent, MDBTabsPane, MDBSelect, MDBTextArea, MDBListGroup, MDBListGroupItem } from 'mdb-react-ui-kit';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import ReactCountryFlag from "react-country-flag";
import { useTranslation } from "react-i18next";
import { Editor } from 'react-draft-wysiwyg';
import { ContentState, EditorState, convertFromHTML } from 'draft-js';
import { convertHTMlToRaw } from "../../utils/HTMLDraft";
import { generateClient } from 'aws-amplify/api';
import { Link, useNavigate, useParams } from "react-router-dom";
import { getLocalizedText } from "../../utils/localizedText";
import { useIndustry } from "../../utils/industryContextHook";
import TimeRangePriceModal, { TimeRangePriceButton } from './TimeRangePriceModal';
import useDebounce from "../../utils/debounce";
import { captureError } from "../../utils/sentryHelpers";
import ErrorFallback from "../../components/toolbox/ErrorFallback";
import { isValidVariantCode, normalizeBoothNumber, normalizeVariant } from "../../utils/boothMap";
import { ConfirmModal } from "../mediathek/MediathekShared";

const client = generateClient();

// Same duplicate check as the booth mask (EditBooth): booths are matched to hall-plan areas by number.
const BOOTH_NUMBERS_BY_TYPE_QUERY = /* GraphQL */ `
  query BoothNumbersByType($type: String!, $limit: Int, $nextToken: String) {
    productsByType(type: $type, filter: { boothNumber: { attributeExists: true } }, limit: $limit, nextToken: $nextToken) {
      items {
        id
        sku
        boothNumber
        name {
          language
          text
        }
      }
      nextToken
    }
  }
`;

const fetchAllPages = async (query, rootField, variables = {}) => {
  const items = [];
  let nextToken = null;
  do {
    const response = await client.graphql({ query, variables: { ...variables, nextToken } });
    const page = response.data[rootField];
    items.push(...(page?.items || []).filter(Boolean));
    nextToken = page?.nextToken || null;
  } while (nextToken);
  return items;
};

/** Display form of a booth number: trimmed, inner whitespace collapsed (case kept), '' -> null. */
const cleanBoothNumberInput = (value) => (value ?? "").toString().trim().replace(/\s+/g, " ") || null;

/**
 * Product.boothVariantAliases from the form: an array (as loaded) or comma-separated text -> list of codes
 * (trimmed, duplicates and the variant code itself removed); null when empty (never stored as []).
 */
const parseVariantAliases = (value, variant) => {
  const parts = Array.isArray(value) ? value : String(value ?? "").split(/[,;\n]/);
  const seen = new Set([normalizeVariant(variant)].filter(Boolean));
  const list = [];
  parts.forEach((part) => {
    const code = cleanBoothNumberInput(part);
    const key = normalizeVariant(code);
    if (!code || seen.has(key)) return;
    seen.add(key);
    list.push(code);
  });
  return list.length > 0 ? list : null;
};

/** Aliases as shown in the text input (array as loaded, or the text being typed). */
const variantAliasesText = (value) => (Array.isArray(value) ? value.filter(Boolean).join(", ") : value ?? "");

/** SKU suggestion of booth products: 'CP-ST-' + normalized booth number or variant code ('' without one). */
const suggestedBoothSku = (boothNumber, boothVariant) => {
  const key = normalizeBoothNumber(boothNumber) || normalizeVariant(boothVariant);
  return key ? `CP-ST-${key.replace(/\s+/g, "-")}` : "";
};

// Product types that are booths on the interactive hall plan (Product.boothNumber etc.).
const BOOTH_TYPES = ["event_booth", "event_space"];
const BOOTH_DIMENSION_FIELDS = ["boothArea", "boothWidth", "boothDepth", "boothHeight"];

/** '' / null -> null, "12,5" -> 12.5, garbage -> NaN. */
const parseBoothDecimal = (value) => {
  if (value === null || value === undefined) return null;
  const text = String(value).trim().replace(",", ".");
  if (text === "") return null;
  const n = Number(text);
  return Number.isFinite(n) ? n : NaN;
};

/** Product.includes (one {language, text} per bullet and language) -> rows [{ de, en }], paired by index per language. */
const includeRowsOf = (includes) => {
  const list = Array.isArray(includes) ? includes.filter(Boolean) : [];
  const de = list.filter((entry) => entry.language === "de");
  const en = list.filter((entry) => entry.language === "en");
  return Array.from({ length: Math.max(de.length, en.length) }, (_, i) => ({ de: de[i]?.text || "", en: en[i]?.text || "" }));
};

/** rows -> flat includes with BOTH languages per row, empty texts kept (editing state, keeps rows paired). */
const flattenIncludeRows = (rows) =>
  rows.flatMap((row) => [{ language: "de", text: row.de || "" }, { language: "en", text: row.en || "" }]);

/** includes for saving: entries with empty text dropped (the order per language is kept). */
const cleanIncludes = (includes) =>
  (Array.isArray(includes) ? includes : [])
    .filter((entry) => entry && typeof entry.text === "string" && entry.text.trim() !== "")
    .map((entry) => ({ language: entry.language, text: entry.text }));

/** Component quantity from the input: whole number >= 1, otherwise null (not saved). */
const parseComponentQty = (value) => {
  const text = String(value ?? "").trim();
  if (text === "") return null;
  const n = Number(text);
  return Number.isInteger(n) && n >= 1 ? n : null;
};

/** Copy of obj without key (same object when the key is missing). */
const omitKey = (obj, key) => {
  if (!obj || !(key in obj)) return obj;
  const next = { ...obj };
  delete next[key];
  return next;
};

// Component quantity is saved this long after the last keystroke (or on blur).
const COMPONENT_QTY_SAVE_DELAY_MS = 400;

const CreateProductPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const { availableIndustries } = useIndustry();
  const { productId } = useParams();

  const { t, i18n } = useTranslation();
  const [productData, setProductData] = useState({});

  // Toast (top right) for save results and component mutation errors: { color, message, key, open } or null.
  const [toast, setToast] = useState(null);
  const showToast = (color, message) => setToast({ color, message, key: Date.now(), open: true });

  const [activeTab, setActiveTab] = useState("general");

  const handleClickTab = (tab) => {
    if (tab !== activeTab) {
      setActiveTab(tab);
    }
  }

  const [categories, setCategories] = useState([]);
  const getCategories = async () => {
    try {
      const categoryRes = await client.graphql({
        query: /* GraphQL */ `
          query ListProductCategorys(
            $filter: ModelProductCategoryFilterInput
            $limit: Int
            $nextToken: String
          ) {
            listProductCategories(filter: $filter, limit: $limit, nextToken: $nextToken) {

              items {
                id
                name {
                  language
                  text
                }
                customerTypes
                description {
                  language
                  text
                }
                sortKey
              }
              nextToken
            }
          }
        `,
      });
      const categories = categoryRes.data.listProductCategories.items;
      setCategories(categories);
    } catch (error) {
      console.warn("Error getting categories", error);
      captureError(error, {
        feature: 'product-management',
        operation: 'fetch-categories',
        tags: { component: 'CreateProductPage' }
      });
    }
  };

  const [selectedIndustryIds, setSelectedIndustryIds] = useState([]);

  const getProductData = async () => {
    try {
      const productRes = await client.graphql({
        variables: { id: productId },
        query: /* GraphQL */ `
          query GetProduct($id: ID!) {
            getProduct(id: $id) {
              id
              sku
              name {
                language
                text
              }
              invoiceName {
                language
                text
              }
              description {
                language
                text
              }
              includes {
                language
                text
              }
              icon
              requiredShipping
              allowedBuyerGroups
              price
              downPaymentPrice
              stock
              type
              subType
              visibleInShop
              productCategoryProductsId
              boothNumber
              boothVariant
              boothVariantAliases
              boothArea
              boothWidth
              boothDepth
              boothHeight

              components {
                items {
                  id
                  componentProductID
                  qty
                  order
                }
              }
              image {
                main {
                  fileName
                }
              }
              files {
                fileName
              }
              eventProducts(limit: 1000) {
                items {
                  id
                  event {
                    id
                    name {
                      language
                      text
                    }
                  }
                  price
                  timeRangePrices {
                    timeRange {
                      start
                      end
                    }
                    price
                  }
                  stock
                  isAvailable
                }
              }
              availableInIndustries(limit: 1000) {
                items {
                  id
                  industryId
                }
              }
            }
          }
          
        `,
      });
      //console.log(productRes);
      const dbProduct = productRes.data.getProduct;
      if (!dbProduct) throw new Error("Product not found: " + productId);
      const productData = {
        ...dbProduct,
        componentIds: dbProduct.components?.items?.map((c) => c.componentProductID) ?? [],
        components: dbProduct.components?.items ?? [],
      }
      return productData
    } catch (error) {
      console.warn("Error getting product data", error);
      captureError(error, {
        feature: 'product-management',
        operation: 'fetch-product-data',
        context: { productId },
        tags: { component: 'CreateProductPage' }
      });
    }
  };

  const intialLoadProduct = async () => {
    console.log("loading product");
    setIsLoading(true);
    const dbProduct = await getProductData();
    if (!dbProduct) {
      setIsLoading(false);
      setLoadError(true);
      return;
    }
    setProductData(dbProduct);
    // Populate selected industries from the loaded product so that saving
    // without touching the industry select does not wipe existing connections.
    setSelectedIndustryIds(
      dbProduct.availableInIndustries?.items?.map((item) => item.industryId) ?? []
    );
    if (dbProduct.description) {
      const deContent = dbProduct.description.find(c => c.language === "de")?.text;
      const enContent = dbProduct.description.find(c => c.language === "en")?.text;
      setEditorState({
        de: deContent ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(deContent)
          )
        ) : EditorState.createEmpty(),
        en: enContent ? EditorState.createWithContent(
          ContentState.createFromBlockArray(
            convertFromHTML(enContent)
          )
        ) : EditorState.createEmpty(),
      });
    }
    setIsLoading(false);
  };

  useEffect(() => {
    console.log("productId", productId)
    if (!productId) {
      console.log("No product id provided");
      getCategories();
      return;
    } else {
      intialLoadProduct();
      getCategories();
    }
  }, []);



  const [validationErrors, setValidationErrors] = useState([]);

  const validateProduct = () => {
    const errors = [];
    if (!productData.sku) {
      errors.push(t("Please enter a SKU"));
    }
    if (!productData.type) {
      errors.push(t("Please select a product type"));
    }
    if (!productData.name || productData.name.length === 0) {
      errors.push(t("Please enter a product name"));
    }
    if (BOOTH_TYPES.includes(productData.type)
      && BOOTH_DIMENSION_FIELDS.some((field) => {
        const n = parseBoothDecimal(productData[field]);
        return Number.isNaN(n) || (n !== null && n < 0);
      })) {
      errors.push(t("Booth dimensions must be numbers of 0 or more (e.g. 12.5 or 12,5)"));
    }
    if (BOOTH_TYPES.includes(productData.type)) {
      // a booth product has EITHER a booth number (single booth) OR an offer code (all booths with this plan code)
      const boothVariant = cleanBoothNumberInput(productData.boothVariant);
      const aliases = parseVariantAliases(productData.boothVariantAliases, boothVariant) || [];
      // booth numbers: any scheme from the SVG (decision 14.10.2026), only a leading "#" is reserved for unnamed shapes
      if ((cleanBoothNumberInput(productData.boothNumber) || "").startsWith("#")) {
        errors.push(t('Booth numbers must not start with "#" (reserved for unnamed shapes).'));
      }
      if (cleanBoothNumberInput(productData.boothNumber) && boothVariant) {
        errors.push(t("A booth product has either a booth number (single booth) or an offer code (all booths with this plan code), not both."));
      }
      if (boothVariant && !isValidVariantCode(boothVariant)) {
        errors.push(t('The offer code may only contain letters, digits, ".", "_" and "-" (max. 16 characters).'));
      }
      if (aliases.length > 0 && !boothVariant) {
        errors.push(t("Aliases are only possible for an offer code."));
      }
      if (aliases.some((alias) => !isValidVariantCode(alias))) {
        errors.push(t('Every alias may only contain letters, digits, ".", "_" and "-" (max. 16 characters).'));
      }
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  // Other booth products with the same (normalized) booth number; null while no warning is shown.
  const [duplicateBooths, setDuplicateBooths] = useState(null);
  const [checkingBoothNumber, setCheckingBoothNumber] = useState(false);

  const findDuplicateBooths = async () => {
    const key = normalizeBoothNumber(productData.boothNumber);
    if (!BOOTH_TYPES.includes(productData.type) || !key) return [];
    try {
      const lists = await Promise.all(
        BOOTH_TYPES.map((type) =>
          fetchAllPages(BOOTH_NUMBERS_BY_TYPE_QUERY, "productsByType", { type, limit: 500 })
        )
      );
      return lists
        .flat()
        .filter((product) => product.id !== productId && normalizeBoothNumber(product.boothNumber) === key);
    } catch (error) {
      // non-blocking: the check is only a warning
      console.warn("Error checking booth number uniqueness", error);
      captureError(error, {
        feature: 'product-management',
        operation: 'check-booth-number-unique',
        context: { productId, boothNumber: productData.boothNumber },
        tags: { component: 'CreateProductPage' }
      });
      return [];
    }
  };

  const createProduct = async () => {
    if (!validateProduct()) {
      return;
    }
    setCheckingBoothNumber(true);
    const others = await findDuplicateBooths();
    setCheckingBoothNumber(false);
    if (others.length > 0) {
      setDuplicateBooths(others);
      return;
    }
    await saveProduct();
  };

  const saveProduct = async () => {
    setDuplicateBooths(null);
    try {
      setIsLoading(true);

      // Create or update the product first
      const response = await client.graphql({
        query: productId ? /* GraphQL */ `
        mutation UpdateProduct($input: UpdateProductInput!) {
          updateProduct(input: $input) {
            id  
          }
        }
        ` : /* GraphQL */ `
        mutation CreateProduct(
          $input: CreateProductInput!
          $condition: ModelProductConditionInput
        ) {
          createProduct(input: $input, condition: $condition) {
            id
          }
        }
        `,
        variables: {
          input: {
            sku: productData.sku,
            name: productData.name,
            visibleInShop: productData.visibleInShop,
            productCategoryProductsId: productData.productCategoryProductsId,
            invoiceName: productData.invoiceName,
            description: productData.description,
            includes: cleanIncludes(productData.includes),
            icon: productData.icon,
            requiredShipping: productData.requiredShipping,
            allowedBuyerGroups: productData.allowedBuyerGroups,
            price: productData.price,
            downPaymentPrice: productData.downPaymentPrice,
            stock: productData.stock,
            image: productData.image,
            type: productData.type,
            subType: productData.subType,
            // Booth fields only for booth types, so non-booth products stay untouched.
            ...(BOOTH_TYPES.includes(productData.type) ? {
              boothNumber: cleanBoothNumberInput(productData.boothNumber),
              // number XOR variant (validated); empty values are stored as null, never ''
              boothVariant: cleanBoothNumberInput(productData.boothVariant),
              boothVariantAliases: cleanBoothNumberInput(productData.boothVariant)
                ? parseVariantAliases(productData.boothVariantAliases, productData.boothVariant)
                : null,
              boothArea: parseBoothDecimal(productData.boothArea),
              boothWidth: parseBoothDecimal(productData.boothWidth),
              boothDepth: parseBoothDecimal(productData.boothDepth),
              boothHeight: parseBoothDecimal(productData.boothHeight),
            } : {}),
            ...(productId ? { id: productId } : {}),
          },
        },
      });

      const currentProductId = productId || response.data[productId ? 'updateProduct' : 'createProduct'].id;

      // Handle industry connections
      // First, get existing industry connections
      const existingIndustries = await client.graphql({
        query: `
        query ListAvailableProductsInIndustries($filter: ModelAvailableProductsInIndustriesFilterInput) {
          listAvailableProductsInIndustries(filter: $filter) {
            items {
              id
              industryId
            }
          }
        }
      `,
        variables: {
          filter: { productId: { eq: currentProductId } }
        }
      });

      const existingIndustryIds = existingIndustries.data.listAvailableProductsInIndustries.items.map(item => item.industryId);

      // Determine which industries to add and remove
      const industriesToAdd = selectedIndustryIds.filter(id => !existingIndustryIds.includes(id));
      const industriesToRemove = existingIndustries.data.listAvailableProductsInIndustries.items
        .filter(item => !selectedIndustryIds.includes(item.industryId));

      // Remove old connections
      await Promise.all(industriesToRemove.map(async item => {
        await client.graphql({
          query: `
            mutation DeleteAvailableProductsInIndustries($input: DeleteAvailableProductsInIndustriesInput!) {
              deleteAvailableProductsInIndustries(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: { id: item.id }
          }
        });
      }));

      // Add new connections
      await Promise.all(industriesToAdd.map(async industryId => {
        await client.graphql({
          query: `
            mutation CreateAvailableProductsInIndustries($input: CreateAvailableProductsInIndustriesInput!) {
              createAvailableProductsInIndustries(input: $input) {
                id
              }
            }
          `,
          variables: {
            input: {
              productId: currentProductId,
              industryId: industryId
            }
          }
        });
      }));

      // Existing product: stay on the page (components, media etc. are edited here as well).
      if (productId) {
        setIsLoading(false);
        showToast("success", t("Product saved."));
        return;
      }

      // New product: back to the list after successful creation
      setTimeout(() => {
        navigate(`/admin/products`);
        setIsLoading(false);
      }, 2000);

    } catch (error) {
      setIsLoading(false);
      showToast("danger", t("Could not save the product."));
      console.error("Error creating/updating product:", error);
      captureError(error, {
        feature: 'product-management',
        operation: productId ? 'update-product' : 'create-product',
        context: { productId, productData },
        tags: { component: 'CreateProductPage' }
      });
    }
  };


  // Editor
  const [editorState, setEditorState] = useState({
    de: EditorState.createEmpty(),
    en: EditorState.createEmpty(),
  })
  const onEditorStateChange = (editorState, language) => {
    setEditorState(oldState => ({ ...oldState, [language]: editorState }));
    setProductData(oldData => ({ ...oldData, description: [...(oldData.description?.length > 0 ? oldData.description.filter(c => c.language !== language) : []), { language, text: convertHTMlToRaw(editorState) }] }));

  }

  // Image and Script

  const [isUploadingLectureFiles, setIsUploadingLectureFiles] = useState(false);
  const [uploadingLecturesErrors, setUploadingLecturesErrors] = useState(null);

  const handleSetFile = (file) => {
    // upload file using mutation: uploadLectureFile(base64FileBuffer: String, companyId: String): LambdaS3Response @function(name: "uploadLectureFiles-${env}")
    if (file) {
      try {
        setIsUploadingLectureFiles(true);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = error => {
          console.log('Error: ', error);
          setUploadingLecturesErrors(error);
        }
        reader.onload = async () => {
          try {
            const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
            const response = await client.graphql({
              query: /* GraphQL */ `
        mutation UploadLectureFile($base64FileBuffer: String!, $companyId: String!) {
          uploadLectureFile(base64FileBuffer: $base64FileBuffer, companyId: $companyId) {
            message
            fileName
          }
        }
      `,
              variables: {
                base64FileBuffer,
              },
            });
            console.log("response upload file", response);
            setProductData(oldData => ({ ...oldData, file: { fileName: response.data.uploadLectureFile.fileName, type: "productFile" } }));

          } catch (error) {
            console.log("error", error);
            captureError(error, {
              feature: 'product-management',
              operation: 'upload-product-file',
              context: { file: file?.name },
              tags: { component: 'CreateProductPage' }
            });
            setIsUploadingLectureFiles(false);
            setUploadingLecturesErrors(error.errors[0].message);
          }
        };

        setIsUploadingLectureFiles(false);
        setUploadingLecturesErrors(null);
      } catch (error) {
        console.log("error", error);
        captureError(error, {
          feature: 'product-management',
          operation: 'upload-product-file-outer',
          context: { file: file?.name },
          tags: { component: 'CreateProductPage' }
        });
        setIsUploadingLectureFiles(false);
        setUploadingLecturesErrors(error.errors[0].message);
      }

    }

  }

  const [isUploadingLectureImage, setIsUploadingLectureImage] = useState(false);
  const [uploadingLectureImageError, setUploadingLectureImageError] = useState(null);

  const handleSetImage = (file) => {
    // upload file using mutation: uploadLectureFile(base64FileBuffer: String, companyId: String): LambdaS3Response @function(name: "uploadLectureFiles-${env}")
    if (file) {
      try {
        setIsUploadingLectureImage(true);
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onerror = error => {
          console.log('Error reader: ', error);
          setUploadingLectureImageError(error);
        }
        reader.onload = async () => {
          try {
            console.log(reader.result)
            const base64FileBuffer = reader.result.replace(/^data:.+;base64,/, '');
            const response = await client.graphql({
              query: /* GraphQL */ `
          mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
            uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
          }
        `,
              variables: {
                imageBuffer: base64FileBuffer,
                imageType: "productImage",
              },
            });
            console.log("response", response);
            const fileName = response.data.uploadCompanyImage.replace(/{|}|fileName=/g, '');
            setProductData(oldData => ({ ...oldData, image: { main: { fileName, alt: "Lecture Image" } } }));

          } catch (error) {
            console.log("error", error);
            captureError(error, {
              feature: 'product-management',
              operation: 'upload-product-image',
              context: { file: file?.name },
              tags: { component: 'CreateProductPage' }
            });
            setIsUploadingLectureImage(false);
            setUploadingLectureImageError(error.errors[0].message);
          }
        }
        setIsUploadingLectureImage(false);
        setUploadingLectureImageError(null);
      } catch (error) {
        console.log("error", error);
        captureError(error, {
          feature: 'product-management',
          operation: 'upload-product-image-outer',
          context: { file: file?.name },
          tags: { component: 'CreateProductPage' }
        });
        setIsUploadingLectureImage(false);
        setUploadingLectureImageError(error.errors[0].message);
      }
    }
  }

  const [orderWithProduct, setOrderWithProduct] = useState([]);

  // Components Tab

  // Components Tab
  const [isLoadingComponents, setIsLoadingComponents] = useState(false);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const filterProducts = (term) => {
    setSearchTerm(term);
    if (!term.trim()) {
      setFilteredProducts(products);
      return;
    }

    const searchValue = term.toLowerCase().trim();
    const filtered = products.filter(product => {
      // Search in all language versions of the name
      const nameMatch = (product.name || []).some(n =>
        (n?.text || "").toLowerCase().includes(searchValue)
      );
      // Search in SKU
      const skuMatch = (product.sku || "").toLowerCase().includes(searchValue);

      return nameMatch || skuMatch;
    });

    setFilteredProducts(filtered);
  };

  const getProducts = async () => {
    setIsLoading(true);
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
      query ListProducts(
        $filter: ModelProductFilterInput
        $limit: Int
        $nextToken: String
      ) {
        listProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
          items {
            id
            sku
            name {
              language
              text
            }
            ean
            type
            subType
            price
            downPaymentPrice
            allowedBuyerGroups
            requiredShipping
            icon
            stock
            createdAt
            updatedAt
            bundleBoothAvaiableAccessoriesId
            bundleBoothIncludedAccessoriesId
            __typename
          }
          nextToken
          __typename
        }
      }
    `,
        variables: {
          limit: 1000
        },
      });
      setProducts(response.data.listProducts.items);
      setFilteredProducts(response.data.listProducts.items);
    } catch (error) {
      console.error('Error fetching products:', error);
      captureError(error, {
        feature: 'product-management',
        operation: 'fetch-products-list',
        tags: { component: 'CreateProductPage' }
      });
    } finally {
      setIsLoading(false);
    }
  };

  /** "SKU · type · subType · price" line of a component product (search results and selected list). */
  const formatComponentPrice = (price) => {
    if (price === null || price === undefined || price === "" || !Number.isFinite(Number(price))) return t("No price");
    return Number(price).toLocaleString(i18n.language?.startsWith("de") ? "de-DE" : "en-GB", { style: "currency", currency: "EUR" });
  };
  const componentMetaLine = (product) =>
    [product.sku, product.type, product.subType, formatComponentPrice(product.price)].filter(Boolean).join(" · ");

  // One add/remove mutation at a time: double clicks must not send a second create/delete.
  const [componentMutationRunning, setComponentMutationRunning] = useState(false);
  const componentMutationRunningRef = useRef(false);
  const runComponentMutation = async (mutate) => {
    if (componentMutationRunningRef.current) return;
    componentMutationRunningRef.current = true;
    setComponentMutationRunning(true);
    try {
      await mutate();
    } finally {
      componentMutationRunningRef.current = false;
      setComponentMutationRunning(false);
    }
  };

  const addProductComponents = (componentProductID) => runComponentMutation(async () => {
    if (!productData.id) {
      showToast("danger", t("Please save the product first, then add components."));
      return;
    }
    if ((productData.componentIds || []).includes(componentProductID)) return;
    try {
      const newProductComponent = await client.graphql({
        query: /* GraphQL */ `
      mutation CreateProductComponent(
        $input: CreateProductComponentInput!
      ) {
        createProductComponent(input: $input) {
          id
          componentProductID
          qty
          order
        }
      }
    `,
        variables: {
          input: {
            parentProductID: productData.id,
            componentProductID,
            qty: 1,
            order: (productData.components?.length || 0) + 1,
          },
        },
      });
      const created = newProductComponent?.data?.createProductComponent;
      if (!created) throw new Error(`createProductComponent returned no row for component product ${componentProductID}`);
      setProductData(oldData => ({
        ...oldData,
        componentIds: [...(oldData.componentIds || []), created.componentProductID],
        components: [...(oldData.components || []), created]
      }));
    } catch (error) {
      console.error('Error adding product component:', error);
      showToast("danger", t("Could not add the component. Please reload the page and try again."));
      captureError(error, {
        feature: 'product-management',
        operation: 'add-product-component',
        context: { productId: productData.id, componentProductID },
        tags: { component: 'CreateProductPage' }
      });
    }
  });

  /** Removes a component row; productComponentId is the ProductComponent id (not the component product id). */
  const removeProductComponents = (productComponentId) => runComponentMutation(async () => {
    if (!productComponentId) {
      showToast("danger", t("This component is not linked to the product any more. Please reload the page."));
      return;
    }
    try {
      const deleteRes = await client.graphql({
        query: /* GraphQL */ `
      mutation DeleteProductComponent(
        $input: DeleteProductComponentInput!
      ) {
        deleteProductComponent(input: $input) {
          id
          componentProductID
        }
      }
    `,
        variables: {
          input: {
            id: productComponentId,
          },
        },
      });
      // null = no row with this id (already deleted or a wrong id): keep the state and tell the user.
      const deleted = deleteRes?.data?.deleteProductComponent;
      if (!deleted) throw new Error(`deleteProductComponent returned no row for id ${productComponentId}`);
      discardComponentQtyEdit(deleted.id);
      setProductData(oldData => {
        const components = (oldData.components || []).filter((c) => c.id !== deleted.id);
        return {
          ...oldData,
          components,
          componentIds: components.map((c) => c.componentProductID),
        };
      });

    } catch (error) {
      console.error('Error removing product component:', error);
      showToast("danger", t("Could not remove the component. Please reload the page and try again."));
      captureError(error, {
        feature: 'product-management',
        operation: 'remove-product-component',
        context: { productId: productData.id, productComponentId },
        tags: { component: 'CreateProductPage' }
      });
    }
  });

  /** "Remove" in the search list: resolves the ProductComponent row of that component product. */
  const removeComponentOfProduct = (componentProductID) => {
    const row = (productData.components || []).find((c) => c.componentProductID === componentProductID);
    return removeProductComponents(row?.id);
  };

  // Quantity input: local draft per ProductComponent id, saved COMPONENT_QTY_SAVE_DELAY_MS after the last
  // keystroke or on blur. Only the latest request per row may update the state (responses can overtake).
  const [componentQtyDrafts, setComponentQtyDrafts] = useState({});
  const componentQtyPendingRef = useRef({}); // id -> { value, timer }
  const componentQtySeqRef = useRef({}); // id -> number of the latest request

  const discardComponentQtyEdit = (productComponentId) => {
    const pending = componentQtyPendingRef.current[productComponentId];
    if (pending) clearTimeout(pending.timer);
    delete componentQtyPendingRef.current[productComponentId];
    componentQtySeqRef.current[productComponentId] = (componentQtySeqRef.current[productComponentId] || 0) + 1;
    setComponentQtyDrafts((drafts) => omitKey(drafts, productComponentId));
  };

  const flushComponentQty = (productComponentId) => {
    const pending = componentQtyPendingRef.current[productComponentId];
    if (!pending) return;
    clearTimeout(pending.timer);
    delete componentQtyPendingRef.current[productComponentId];
    const qty = parseComponentQty(pending.value);
    if (qty === null) return; // invalid input is not saved; blur shows the stored quantity again
    updateProductComponentQTY(productComponentId, qty);
  };

  const handleComponentQtyChange = (productComponentId, value) => {
    setComponentQtyDrafts((drafts) => ({ ...drafts, [productComponentId]: value }));
    const pending = componentQtyPendingRef.current[productComponentId];
    if (pending) clearTimeout(pending.timer);
    componentQtyPendingRef.current[productComponentId] = {
      value,
      timer: setTimeout(() => flushComponentQty(productComponentId), COMPONENT_QTY_SAVE_DELAY_MS),
    };
  };

  const handleComponentQtyBlur = (productComponentId) => {
    flushComponentQty(productComponentId);
    setComponentQtyDrafts((drafts) =>
      productComponentId in drafts && parseComponentQty(drafts[productComponentId]) === null
        ? omitKey(drafts, productComponentId)
        : drafts
    );
  };

  // Leaving the page within the save delay still saves the last typed quantity.
  const flushComponentQtyRef = useRef(flushComponentQty);
  flushComponentQtyRef.current = flushComponentQty;
  useEffect(() => () => {
    Object.keys(componentQtyPendingRef.current).forEach((id) => flushComponentQtyRef.current(id));
  }, []);

  const updateProductComponentQTY = async (productComponentId, qty) => {
    const seq = (componentQtySeqRef.current[productComponentId] || 0) + 1;
    componentQtySeqRef.current[productComponentId] = seq;
    const isLatest = () => componentQtySeqRef.current[productComponentId] === seq;
    try {
      const updateRes = await client.graphql({
        query: /* GraphQL */ `
      mutation UpdateProductComponent(
        $input: UpdateProductComponentInput!
      ) {
        updateProductComponent(input: $input) {
          id
          componentProductID
          qty
        }
      }
    `,
        variables: {
          input: {
            id: productComponentId,
            qty,
          },
        },
      });
      const updated = updateRes?.data?.updateProductComponent;
      if (!updated) throw new Error(`updateProductComponent returned no row for id ${productComponentId}`);
      if (!isLatest()) return; // a newer quantity is on its way
      setProductData(oldData => ({
        ...oldData,
        // merge: this response has no `order`, replacing the row would lose the sort position
        components: (oldData.components || []).map((c) => c.id === updated.id ? { ...c, ...updated } : c)
      }));
      setComponentQtyDrafts((drafts) =>
        parseComponentQty(drafts[productComponentId]) === updated.qty ? omitKey(drafts, productComponentId) : drafts
      );
    } catch (error) {
      console.error('Error updating product component quantity:', error);
      if (isLatest()) {
        if (!componentQtyPendingRef.current[productComponentId]) {
          setComponentQtyDrafts((drafts) => omitKey(drafts, productComponentId));
        }
        showToast("danger", t("Could not save the quantity. Please reload the page and try again."));
      }
      captureError(error, {
        feature: 'product-management',
        operation: 'update-product-component-qty',
        context: { productComponentId, qty },
        tags: { component: 'CreateProductPage' }
      });
    }
  };

  const updateProductComponentOrder = async (productComponentId, order) => {
    try {
      const updateRes = await client.graphql({
        query: /* GraphQL */ `
      mutation UpdateProductComponent(
        $input: UpdateProductComponentInput!
      ) {
        updateProductComponent(input: $input) {
          id
          componentProductID
          qty
          order
        }
      }
    `,
        variables: {
          input: {
            id: productComponentId,
            order,
          },
        },
      });
      setProductData(oldData => ({
        ...oldData,
        components: oldData.components.map((c) => c.id === updateRes.data.updateProductComponent.id ? updateRes.data.updateProductComponent : c)
      }));
    } catch (error) {
      console.error('Error updating product to event:', error);
      captureError(error, {
        feature: 'product-management',
        operation: 'update-product-component-order',
        context: { productComponentId, order },
        tags: { component: 'CreateProductPage' }
      });
    }
  };

  const handleComponentDragEnd = (result) => {
    if (!result.destination) return;

    const sortedComponents = [...productData.components].sort((a, b) => (a.order || 0) - (b.order || 0));
    const [reorderedItem] = sortedComponents.splice(result.source.index, 1);
    sortedComponents.splice(result.destination.index, 0, reorderedItem);

    const updatedComponents = sortedComponents.map((c, index) => ({ ...c, order: index + 1 }));
    setProductData(oldData => ({ ...oldData, components: updatedComponents }));

    updatedComponents.forEach((c) => {
      updateProductComponentOrder(c.id, c.order);
    });
  };

  // In Events
  const [events, setEvents] = useState([]);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [timeRangePriceModalOpen, setTimeRangePriceModalOpen] = useState(false);
  const [selectedEventProduct, setSelectedEventProduct] = useState(null);
  const [eventProductUpdates, setEventProductUpdates] = useState({});
  const debouncedEventProductUpdates = useDebounce(eventProductUpdates, 1000);

  useEffect(() => {
    Object.entries(debouncedEventProductUpdates).forEach(([id, update]) => {
      // Call the existing updateEventProduct with debounced values
      updateEventProduct(id, update.price, update.stock, update.timeRangePrices);
    });
    if (Object.keys(debouncedEventProductUpdates).length) {
      setEventProductUpdates(prev => {
        const newUpdates = { ...prev };
        Object.keys(debouncedEventProductUpdates).forEach(key => {
          delete newUpdates[key];
        });
        return newUpdates;
      });
    }
  }, [debouncedEventProductUpdates]);

  const handleEventProductUpdate = (productId, changes) => {
    setEventProductUpdates(prev => ({
      ...prev,
      [productId]: { ...prev[productId], ...changes }
    }));
    setProductData(prevData => ({
      ...prevData,
      eventProducts: {
        ...prevData.eventProducts,
        items: prevData.eventProducts.items.map(item => item.id === productId ? { ...item, ...changes } : item)
      }
    }));
  };

  const getEvents = async () => {
    setIsLoadingEvents(true);
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
        query ListEvents {
          listEvents {
            items {
              id
              name {
                language
                text
              }
              startDate
            }
          }
        }
      `,
      });
      setEvents(response.data.listEvents.items.sort((a, b) => new Date(a.startDate) - new Date(b.startDate)));
    } catch (error) {
      console.error('Error fetching events:', error);
      captureError(error, {
        feature: 'product-management',
        operation: 'fetch-events',
        tags: { component: 'CreateProductPage' }
      });
    } finally {
      setIsLoadingEvents(false);
    }
  };

  const addProductToEvent = async (eventId) => {
    try {
      const response = await client.graphql({
        query: /* GraphQL */ `
      mutation CreateEventProduct(
        $input: CreateEventProductInput!
      ) {
        createEventProduct(input: $input) {
          id
          event {
            id 
            name {
              language
              text
            }
          }
          price
          stock
          isAvailable
        }
      }
    `,
        variables: {
          input: {
            eventEventProductsId: eventId,
            productEventProductsId: productId,
            price: productData.price,
            stock: productData.stock,
            isAvailable: true,
          },
        },
      });
      setProductData(prevData => ({
        ...prevData,
        eventProducts: {
          items: [...(prevData.eventProducts?.items || []), response.data.createEventProduct]
        }
      }));
    } catch (error) {
      console.error('Error adding product to event:', error);
      captureError(error, {
        feature: 'product-management',
        operation: 'add-product-to-event',
        context: { eventId, productId },
        tags: { component: 'CreateProductPage' }
      });
    }
  };

  const removeProductFromEvent = async (eventProductId) => {
    try {
      await client.graphql({
        query: /* GraphQL */ `
      mutation DeleteEventProduct(
        $input: DeleteEventProductInput!
      ) {
        deleteEventProduct(input: $input) {
          id
        }
      }
    `,
        variables: {
          input: {
            id: eventProductId,
          },
        },
      });
      setProductData(prevData => ({
        ...prevData,
        eventProducts: {
          items: prevData.eventProducts.items.filter(item => item.id !== eventProductId)
        }
      }));
    } catch (error) {
      console.error('Error removing product from event:', error);
      captureError(error, {
        feature: 'product-management',
        operation: 'remove-product-from-event',
        context: { eventProductId },
        tags: { component: 'CreateProductPage' }
      });
    }
  };

  const updateEventProduct = async (eventProductId, price, stock, timeRangePrices = undefined) => {
    try {
      // Get current values from state
      const currentItem = productData.eventProducts.items.find(item => item.id === eventProductId) || {};
      const newPrice = (price !== null && price !== undefined && !isNaN(price))
        ? parseFloat(price)
        : parseFloat(currentItem.price || 0);
      const newStock = (stock !== null && stock !== undefined && !isNaN(stock))
        ? parseInt(stock)
        : parseInt(currentItem.stock || 0);

      const response = await client.graphql({
        query: /* GraphQL */ `
      mutation UpdateEventProduct($input: UpdateEventProductInput!) {
        updateEventProduct(input: $input) {
          id
          price
          stock
          isAvailable
          timeRangePrices {
            timeRange {
              start
              end
            }
            price
          }
        }
      }
    `,
        variables: {
          input: {
            id: eventProductId,
            price: newPrice,
            stock: newStock,
            ...(timeRangePrices !== undefined && { timeRangePrices }),
          },
        },
      });

    } catch (error) {
      console.error('Error updating event product:', error);
      captureError(error, {
        feature: 'product-management',
        operation: 'update-event-product',
        context: { eventProductId, price, stock },
        tags: { component: 'CreateProductPage' }
      });
    }
  };



  useEffect(() => {
    if (activeTab === "components" && products.length === 0) {
      getProducts();
    }
    if (activeTab === "events" && events.length === 0) {
      getEvents();
    }
  }, [activeTab]);


  if (loadError) return <ErrorFallback />;

  return (
    <>
      <MDBRow className="h-auto d-flex justify-content-center">
        <MDBCol size="12" xl="12" xxl="11">
          <MDBCard>
            <MDBCardBody className="px-1 px-md-5">
              {validationErrors.length > 0 && (
                <div className="alert alert-danger" role="alert">
                  {validationErrors.map((error) => (
                    <p key={error}>{error}</p>
                  ))}
                </div>
              )}
              <MDBBtn onClick={() => createProduct()} color="primary" className="float-end" disabled={checkingBoothNumber || isLoading}>
                {t("Save")}
              </MDBBtn>
              <MDBCardTitle>{productId ? t("Edit Product") : t("Create Product")}</MDBCardTitle>
              <MDBTabs className='mb-3'>
                <MDBTabsItem>
                  <MDBTabsLink onClick={() => handleClickTab('general')} active={activeTab === 'general'}>
                    <MDBIcon fas icon="cogs" className='me-2' /> {t("General")}
                  </MDBTabsLink>
                </MDBTabsItem>
                <MDBTabsItem>
                  <MDBTabsLink onClick={() => handleClickTab('media')} active={activeTab === 'media'}>
                    <MDBIcon fas icon='image' className='me-2' /> {t("Media")}
                  </MDBTabsLink>
                </MDBTabsItem>
                <MDBTabsItem>
                  <MDBTabsLink onClick={() => handleClickTab('pricing')} active={activeTab === 'pricing'}>
                    <MDBIcon fas icon='dollar' className='me-2' /> {t("Pricing")}
                  </MDBTabsLink>
                </MDBTabsItem>
                <MDBTabsItem>
                  <MDBTabsLink onClick={() => handleClickTab('components')} active={activeTab === 'components'}>
                    <MDBIcon fas icon='shapes' className='me-2' /> {t("Components")}
                  </MDBTabsLink>
                </MDBTabsItem>
                <MDBTabsItem>
                  <MDBTabsLink onClick={() => handleClickTab('events')} active={activeTab === 'events'}>
                    <MDBIcon fas icon='calendar-alt' className='me-2' /> {t("In Events")}
                  </MDBTabsLink>
                </MDBTabsItem>
                <MDBTabsItem>
                  <MDBTabsLink onClick={() => handleClickTab('stock')} active={activeTab === 'stock'}>
                    <MDBIcon fas icon='warehouse' className='me-2' /> {t("Stock")}
                  </MDBTabsLink>
                </MDBTabsItem>
                <MDBTabsItem>
                  <MDBTabsLink onClick={() => handleClickTab('orders')} active={activeTab === 'orders'}>
                    <MDBIcon fas icon='shopping-bag' className='me-2' /> {t("Orders")}
                  </MDBTabsLink>
                </MDBTabsItem>
              </MDBTabs>

              <MDBTabsContent>

                <MDBTabsPane open={activeTab === 'general'}>
                  {/* SKU */}
                  <MDBTypography tag="h5">{t("SKU")}</MDBTypography>
                  <MDBInput
                    label={t("SKU")}
                    type="text"
                    value={productData.sku}
                    onChange={(e) => setProductData({ ...productData, sku: e.target.value })}
                  />
                  {/* SKU End */}
                  <MDBTypography tag="h5">{t("Shop")}</MDBTypography>
                  <MDBRow>
                    <MDBCol size="12" lg="4">
                      <MDBCheckbox
                        name="visibleInShop"
                        id="visibleInShop"
                        label={t("Visible in shop")}
                        checked={productData?.visibleInShop}
                        onChange={(e) => setProductData({ ...productData, visibleInShop: e.target.checked })}
                      />
                    </MDBCol>
                    <MDBCol size="12" lg="4">
                      <MDBSelect
                        label={t("Select Industries")}
                        multiple
                        data={availableIndustries?.map(industry => ({
                          text: getLocalizedText(industry.name),
                          value: industry.id
                        }))}
                        value={selectedIndustryIds || []}
                        onChange={(entries) => setSelectedIndustryIds(entries.map(entry => entry.value))}
                      />
                    </MDBCol>
                    <MDBCol size="12" lg="4">
                      <MDBSelect
                        label={t("Category")}
                        value={productData?.productCategoryProductsId}
                        onChange={(e) => setProductData({ ...productData, productCategoryProductsId: e?.value })}
                        data={[
                          { text: t("Select Category"), value: null },
                          ...categories.map((c) => ({ text: getLocalizedText(c.name), value: c.id }))
                        ]}
                      />
                    </MDBCol>

                  </MDBRow>
                  <MDBRow>
                    <MDBCol size="12" lg="6">

                      {/* Type */}
                      <MDBTypography tag="h5">{t("Type")}</MDBTypography>
                      {/* <MDBInput
  label={t("Type")}
  type="text"
  value={productData.type}
  onChange={(e) => setProductData({...productData, type: e.target.value})}
/> */}
                      <MDBSelect label={t("Type")}
                        onChange={(e) => setProductData({ ...productData, type: e.value })}
                        value={productData.type}
                        data={
                          [
                            { text: t("All"), value: null },
                            { text: t("Event booths"), value: "event_booth" },
                            { text: t("Event spaces"), value: "event_space" },
                            { text: t("Event booths partner"), value: "event_booth_partner" },
                            { text: t("Event equipment"), value: "event_equipment" },
                            { text: t("Marketing"), value: "marketing" },
                            { text: t("VIP"), value: "vip" },
                            { text: t("Marketing Stand"), value: "marketing_booth" },
                            { text: t("Digital Access"), value: "digital_access" },
                            { text: t("Tickets"), value: "ticket" },
                            { text: t("Discount"), value: "discount" },
                          ]
                        }
                      />
                      {/* Type End */}
                    </MDBCol>
                    <MDBCol size="12" lg="6">
                      {/* SubType */}
                      <MDBTypography tag="h5">{t("Subtype")}</MDBTypography>
                      <MDBInput
                        label={t("Subtype")}
                        type="text"
                        value={productData.subType}
                        onChange={(e) => setProductData({ ...productData, subType: e.target.value })}
                      />
                      {/* SubType End */}
                    </MDBCol>
                  </MDBRow>
                  {/* Booth */}
                  {BOOTH_TYPES.includes(productData.type) && (
                    <MDBCard className="border shadow-0 mt-3 mb-3">
                      <MDBCardBody>
                        <MDBTypography tag="h5">
                          <MDBIcon fas icon="map-marked-alt" className="me-2" />
                          {t("Booth")}
                        </MDBTypography>
                        <MDBRow>
                          <MDBCol size="12" lg="4" className="mb-3">
                            <MDBInput
                              label={t("Booth number")}
                              type="text"
                              value={productData.boothNumber ?? ""}
                              onChange={(e) => setProductData({ ...productData, boothNumber: e.target.value })}
                            />
                            <div className="form-text">
                              {t("As printed on the hall plan, any scheme (e.g. D3.5, A-12, H3/B12). Booths with a number appear on the interactive hall plan.")}
                            </div>
                            {/s/.test(String(productData.boothNumber ?? "").trim()) && (
                              <div className="form-text text-warning">
                                {t("The booth number contains spaces; it is matched with collapsed spaces and must be written exactly like that in the SVG.")}
                              </div>
                            )}
                            {String(productData.boothNumber ?? "").trim().length > 32 && (
                              <div className="form-text text-warning">{t("The booth number is longer than 32 characters.")}</div>
                            )}
                          </MDBCol>
                          <MDBCol size="12" lg="4" className="mb-3">
                            <MDBInput
                              label={t("Offer code (hall plan)")}
                              type="text"
                              value={productData.boothVariant ?? ""}
                              onChange={(e) => setProductData({ ...productData, boothVariant: e.target.value })}
                            />
                            <div className="form-text">
                              {t("Either a booth number (one physical booth) or an offer code, e.g. 18B: one offer for all booths of the hall plan with this plan code (data-variant); the exhibitor picks the booth number.")}
                            </div>
                          </MDBCol>
                          <MDBCol size="12" lg="4" className="mb-3">
                            <MDBInput
                              label={t("Offer code aliases (comma-separated)")}
                              type="text"
                              value={variantAliasesText(productData.boothVariantAliases)}
                              disabled={!cleanBoothNumberInput(productData.boothVariant)}
                              onChange={(e) => setProductData({ ...productData, boothVariantAliases: e.target.value })}
                            />
                            <div className="form-text">
                              {t("Other plan codes meaning this offer, e.g. 9 for Z9. Used for matching only.")}
                            </div>
                          </MDBCol>
                          {!String(productData.sku ?? "").trim()
                            && suggestedBoothSku(productData.boothNumber, productData.boothVariant) && (
                            <MDBCol size="12" className="mb-3">
                              <MDBBtn
                                type="button"
                                color="link"
                                size="sm"
                                className="px-0"
                                onClick={() => setProductData({
                                  ...productData,
                                  sku: suggestedBoothSku(productData.boothNumber, productData.boothVariant),
                                })}
                              >
                                {t("Use SKU {{sku}}", { sku: suggestedBoothSku(productData.boothNumber, productData.boothVariant) })}
                              </MDBBtn>
                            </MDBCol>
                          )}
                          <MDBCol size="12" lg="8">
                            <MDBRow>
                              {[
                                { field: "boothArea", label: t("Area (m²)") },
                                { field: "boothWidth", label: t("Width (m)") },
                                { field: "boothDepth", label: t("Depth (m)") },
                                { field: "boothHeight", label: t("Height (m)") },
                              ].map(({ field, label }) => (
                                <MDBCol size="6" md="3" className="mb-3" key={field}>
                                  <MDBInput
                                    label={label}
                                    type="text"
                                    inputMode="decimal"
                                    value={productData[field] ?? ""}
                                    onChange={(e) => setProductData({ ...productData, [field]: e.target.value })}
                                  />
                                </MDBCol>
                              ))}
                            </MDBRow>
                          </MDBCol>
                        </MDBRow>
                        {productId && (
                          <Link to={"/admin/booth/" + productId}>
                            <MDBIcon fas icon="cube" className="me-2" />
                            {t("Manage dimension PDF and 3D model")}
                          </Link>
                        )}
                      </MDBCardBody>
                    </MDBCard>
                  )}
                  {/* Booth End */}
                  {/* Name */}
                  <MDBTypography tag="h5">{t("Name")}</MDBTypography>
                  <MDBInputGroup className="mb-1" textAfter={<span style={productData.name?.find((t) => t.language === "de")?.text?.length === 60 ? { color: "red", fontWeight: "bold" } : {}}>{((productData?.name?.length > 0 ? productData.name.find((t) => t.language === "de")?.text : "")?.length || "0") + "/60"}</span>}>
                    <div className="input-group-text">
                      <ReactCountryFlag
                        countryCode="de"
                        svg
                        style={{
                          width: "20px",
                          height: "20px",
                        }}
                        title={i18n.language}
                      />
                    </div>
                    <MDBInput
                      label={t("German")}
                      type="text"
                      value={productData?.name?.find((t) => t.language === "de")?.text || ""}
                      onChange={(e) => setProductData({ ...productData, name: [{ language: "de", text: e.target.value }, ...(productData.name?.length > 0 ? productData.name.filter((t) => t.language !== "de") : [])] })}
                    />
                  </MDBInputGroup>
                  <MDBInputGroup className="mb-1" textAfter={<span style={productData.name?.find((t) => t.language === "en")?.text?.length === 60 ? { color: "red", fontWeight: "bold" } : {}}>{((productData?.name?.length > 0 ? productData.name.find((t) => t.language === "en")?.text : "")?.length || "0") + "/60"}</span>}>
                    <div className="input-group-text">
                      <ReactCountryFlag
                        countryCode="gb"
                        svg
                        style={{
                          width: "20px",
                          height: "20px",
                        }}
                        title={i18n.language}
                      />
                    </div>
                    <input
                      className="form-control"
                      placeholder={t("English")}
                      type="text"
                      value={productData?.name?.find((t) => t.language === "en")?.text || ""}
                      onChange={(e) => setProductData({ ...productData, name: [{ language: "en", text: e.target.value }, ...(productData.name?.length > 0 ? productData.name.filter((t) => t.language !== "en") : [])] })}
                    />
                  </MDBInputGroup>
                  {/* Name End */}
                  {/* Invoice Name */}
                  <MDBTypography tag="h5" className="mt-3">{t("Invoice Name")}</MDBTypography>
                  <MDBInputGroup className="mb-1" textAfter={<span style={productData.invoiceName?.find((t) => t.language === "de")?.text?.length === 60 ? { color: "red", fontWeight: "bold" } : {}}>{((productData?.invoiceName?.length > 0 ? productData.invoiceName.find((t) => t.language === "de")?.text : "")?.length || "0") + "/60"}</span>}>
                    <div className="input-group-text">
                      <ReactCountryFlag
                        countryCode="de"
                        svg
                        style={{
                          width: "20px",
                          height: "20px",
                        }}
                        title={i18n.language}
                      />
                    </div>
                    <MDBInput
                      label={t("German")}
                      type="text"
                      value={productData?.invoiceName?.find((t) => t.language === "de")?.text || ""}
                      onChange={(e) => setProductData({ ...productData, invoiceName: [{ language: "de", text: e.target.value }, ...(productData.invoiceName?.length > 0 ? productData.invoiceName.filter((t) => t.language !== "de") : [])] })}
                    />
                  </MDBInputGroup>
                  <MDBInputGroup className="mb-1" textAfter={<span style={productData.invoiceName?.find((t) => t.language === "en")?.text?.length === 60 ? { color: "red", fontWeight: "bold" } : {}}>{((productData?.invoiceName?.length > 0 ? productData.invoiceName.find((t) => t.language === "en")?.text : "")?.length || "0") + "/60"}</span>}>
                    <div className="input-group-text">
                      <ReactCountryFlag
                        countryCode="gb"
                        svg
                        style={{
                          width: "20px",
                          height: "20px",
                        }}
                        title={i18n.language}
                      />
                    </div>
                    <input
                      className="form-control"
                      placeholder={t("English")}
                      type="text"
                      value={productData?.invoiceName?.find((t) => t.language === "en")?.text || ""}
                      onChange={(e) => setProductData({ ...productData, invoiceName: [{ language: "en", text: e.target.value }, ...(productData.invoiceName?.length > 0 ? productData.invoiceName.filter((t) => t.language !== "en") : [])] })}
                    />
                  </MDBInputGroup>
                  {/* Invoice Name End */}
                  {/* Topic */}
                  {/* <MDBTypography tag="h5" className="mt-3">{t("Topic")}</MDBTypography>
<div className="w-100 text-center">
<MDBBtn onClick={() => toggleSelectTopicsModal()} color="primary" outline>
  {t("Select Topic")}
</MDBBtn>
<MDBRow className="w-100">
    <MDBCol>
    {topics.length > 0 && topicGroups.length > 0 && productData.cMSProductTopicId && (
        <Topics
        topics={topics}
        topicGroups={topicGroups}
        selectedTopics={[productData.cMSProductTopicId] || []}
        setSelectedTopics={handleSetSelectedTopics}
        />
    )}
    </MDBCol>
</MDBRow>
</div> */}
                  {/* Topic End */}
                  {/* Description */}
                  <div>
                    <MDBTypography tag="h5" className="mt-3">{t("Description")}</MDBTypography>
                    <ReactCountryFlag
                      countryCode="de"
                      svg
                      style={{
                        width: "20px",
                        height: "20px",
                      }}
                      title={i18n.language}
                    />
                    <Editor
                      editorState={editorState["de"]}
                      editorClassName="md-textarea form-control"
                      onEditorStateChange={e => onEditorStateChange(e, "de")}
                      toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                      }}
                    />
                    <br></br>
                    <ReactCountryFlag
                      countryCode="gb"
                      svg
                      style={{
                        width: "20px",
                        height: "20px",
                      }}
                      title={i18n.language}
                    />
                    <Editor
                      editorState={editorState["en"]}
                      editorClassName="md-textarea form-control"
                      onEditorStateChange={e => onEditorStateChange(e, "en")}
                      toolbar={{
                        options: ['inline', 'blockType', 'fontSize', 'list', 'textAlign', 'colorPicker', 'link', 'embedded', 'emoji', 'image', 'remove', 'history'],
                      }}
                    />
                  </div>
                  {/* Description End */}
                  {/* Includes */}
                  <MDBTypography tag="h5" className="mt-3">{t("Includes")}</MDBTypography>
                  {BOOTH_TYPES.includes(productData.type) && (
                    <p className="small text-muted mb-2" data-testid="includes-components-hint">
                      <MDBIcon fas icon="info-circle" className="me-1" />
                      {t("Components (equipment products with quantities, product editor tab Components) are listed to exhibitors automatically. Use this list only for services that are not components, e.g. programme participation or CP-COMPANY.")}
                    </p>
                  )}
                  {(() => {
                    // Rows are edited as DE/EN pairs; empty texts stay in the state while editing so rows neither
                    // vanish nor shift between the languages, and are dropped on save (cleanIncludes).
                    const rows = includeRowsOf(productData.includes);
                    const deIncludes = rows.map(row => ({ language: "de", text: row.de }));
                    const enIncludes = rows.map(row => ({ language: "en", text: row.en }));
                    const rowCount = rows.length;

                    const updateIncludeAt = (rowIndex, language, text) => {
                      setProductData(oldData => {
                        const nextRows = includeRowsOf(oldData.includes);
                        while (nextRows.length <= rowIndex) nextRows.push({ de: "", en: "" });
                        nextRows[rowIndex] = { ...nextRows[rowIndex], [language]: text };
                        return { ...oldData, includes: flattenIncludeRows(nextRows) };
                      });
                    };

                    const removeIncludeRow = (rowIndex) => {
                      setProductData(oldData => ({
                        ...oldData,
                        includes: flattenIncludeRows(includeRowsOf(oldData.includes).filter((_, i) => i !== rowIndex)),
                      }));
                    };

                    return [...Array(rowCount)].map((_, index) => (
                      <MDBRow className="w-100" key={index}>
                        <MDBCol size="12" lg="1" className="pt-2">
                          <span className="fw-bold">{(index + 1)}</span>
                        </MDBCol>
                        <MDBCol size="12" lg="5" className="px-1">
                          <MDBInputGroup className="mb-1 mb-md-3">
                            <div className="input-group-text">
                              <ReactCountryFlag
                                countryCode="de"
                                svg
                                style={{ width: "20px", height: "20px" }}
                              />
                            </div>
                            <MDBTextArea
                              rows={1}
                              type="text"
                              value={deIncludes[index]?.text || ""}
                              onChange={(e) => updateIncludeAt(index, "de", e.target.value)}
                            />
                          </MDBInputGroup>
                        </MDBCol>
                        <MDBCol size="12" lg="5" className="px-1">
                          <MDBInputGroup className="mb-1 mb-md-3">
                            <div className="input-group-text">
                              <ReactCountryFlag
                                countryCode="gb"
                                svg
                                style={{ width: "20px", height: "20px" }}
                              />
                            </div>
                            <MDBTextArea
                              rows={1}
                              type="text"
                              value={enIncludes[index]?.text || ""}
                              onChange={(e) => updateIncludeAt(index, "en", e.target.value)}
                            />
                          </MDBInputGroup>
                        </MDBCol>
                        <MDBCol size="12" lg="1">
                          <MDBBtn onClick={() => removeIncludeRow(index)} color="danger" outline>
                            {t("Remove")}
                          </MDBBtn>
                        </MDBCol>
                      </MDBRow>
                    ));
                  })()}
                  <MDBBtn onClick={() => setProductData(oldData => ({ ...oldData, includes: flattenIncludeRows([...includeRowsOf(oldData.includes), { de: "", en: "" }]) }))} color="primary" outline>
                    {t("Add")}
                  </MDBBtn>
                  {/* Includes End */}
                  {/* <MDBTypography tag="h5" className="mt-3">{t("Features")}</MDBTypography> */}
                  {/* <MDBTypography tag="h5" className="mt-3">{t("Files")}</MDBTypography> */}
                  <MDBTypography tag="h5" className="mt-3">{t("Additonal")}</MDBTypography>
                  <MDBRow>
                    <MDBCol size="12" lg="4">
                      <span className="fw-bold">{t("Requires shipping")}</span>
                      <MDBCheckbox
                        name="requiredShipping"
                        id="requiredShipping"
                        label={t("Shipping required")}
                        checked={productData.requiredShipping}
                        onChange={(e) => setProductData({ ...productData, requiredShipping: e.target.checked })}
                      />
                    </MDBCol>
                    <MDBCol size="12" lg="4">
                      <span className="fw-bold">{t("Allowed buyer groups")}</span>
                      <MDBCheckbox
                        name="allowConsumer"
                        id="allowConsumer"
                        label={t("Consumer")}
                        checked={productData.allowedBuyerGroups?.includes("consumer")}
                        onChange={(e) => setProductData({ ...productData, allowedBuyerGroups: e.target.checked ? [...(productData.allowedBuyerGroups || []), "consumer"] : productData.allowedBuyerGroups.filter(g => g !== "consumer") })}
                      />
                      <MDBCheckbox
                        name="allowBusiness"
                        id="allowBusiness"
                        label={t("Business")}
                        checked={productData.allowedBuyerGroups?.includes("business")}
                        onChange={(e) => setProductData({ ...productData, allowedBuyerGroups: e.target.checked ? [...(productData.allowedBuyerGroups || []), "business"] : productData.allowedBuyerGroups.filter(g => g !== "business") })}
                      />
                    </MDBCol>
                    <MDBCol size="12" lg="4">
                      <span className="fw-bold">{t("Icon")}</span>
                      <MDBInput type="text" value={productData.icon} onChange={(e) => setProductData({ ...productData, icon: e.target.value })} />
                    </MDBCol>
                  </MDBRow>
                </MDBTabsPane>
                <MDBTabsPane open={activeTab === 'media'}>
                  {/* LECTURE IMAGE */}
                  <MDBRow className="d-flex justify-content-evenly">
                    <MDBCol size="12" lg="10" xl="8" xxl="7">
                      <hr className="my-4" />
                      <div className="mb-2 mb-lg-4">

                      </div>
                      <div className="w-100 text-center mb-2 mb-lg-4">
                        <img
                          src={productData?.image?.main?.fileName ?
                            "https://iec-cp-public.s3.eu-central-1.amazonaws.com/ProductImages/" + productData.image.main.fileName : "https://placehold.co/600x400"}
                          className="img-thumbnail"
                          style={{ height: "200px", width: "400px", objectFit: "cover" }}
                          alt='...' />
                      </div>
                      <MDBFile id='fileDE' onChange={e => handleSetImage(e.target.files[0])} />

                    </MDBCol>

                  </MDBRow>
                  {/* LECTURE IMAGE END */}
                </MDBTabsPane>
                <MDBTabsPane open={activeTab === 'pricing'}>
                  {/* Price and downPaymentPrice */}
                  <MDBRow className="d-flex justify-content-evenly">
                    <MDBCol size="12" lg="10" xl="8" xxl="7">
                      <MDBRow>
                        <MDBCol size="12" lg="6">
                          <MDBInputGroup className="mb-3">
                            <MDBInput
                              label={t("Price")}
                              type="number"
                              value={productData.price}
                              onChange={(e) => setProductData({ ...productData, price: e.target.value })}
                            />
                          </MDBInputGroup>
                        </MDBCol>
                        <MDBCol size="12" lg="6">
                          <MDBInputGroup className="mb-3">
                            <MDBInput
                              label={t("Down Payment")}
                              type="number"
                              value={productData.downPaymentPrice}
                              onChange={(e) => setProductData({ ...productData, downPaymentPrice: e.target.value })}
                            />
                          </MDBInputGroup>
                        </MDBCol>
                      </MDBRow>
                    </MDBCol>
                  </MDBRow>
                </MDBTabsPane>
                <MDBTabsPane open={activeTab === 'components'}>
                  {/* Components */}

                  <MDBRow>
                    <MDBCol size="12" lg="6">
                      <MDBTypography tag="h5">{t("Products")}</MDBTypography>
                      <MDBInput
                        type="text"
                        label={t("Search by name or SKU")}
                        className="mb-3"
                        value={searchTerm}
                        onChange={(e) => filterProducts(e.target.value)}
                      />
                      <MDBListGroup>
                        {(filteredProducts).map((product) => (
                          <MDBListGroupItem key={product.id}>
                            <MDBRow>
                              <MDBCol size="12" lg="9">
                                {product.name?.find(n => n.language === i18n.language)?.text || product.name?.[0]?.text}
                                <br />
                                <span className="small text-muted">{componentMetaLine(product)}</span>
                              </MDBCol>
                              <MDBCol size="12" lg="3">
                                {productData.componentIds?.includes(product.id) ? (
                                  <MDBBtn onClick={() => removeComponentOfProduct(product.id)} color="danger" outline disabled={componentMutationRunning}>
                                    {t("Remove")}
                                  </MDBBtn>
                                ) : (
                                  <MDBBtn onClick={() => addProductComponents(product.id)} color="primary" outline disabled={componentMutationRunning}>
                                    {t("Add")}
                                  </MDBBtn>
                                )}
                              </MDBCol>
                            </MDBRow>
                          </MDBListGroupItem>
                        ))}
                      </MDBListGroup>
                    </MDBCol>
                    <MDBCol size="12" lg="6">
                      <MDBTypography tag="h5">{t("Selected Products")}</MDBTypography>
                      <p className="text-muted small">{t('Drag to reorder')}</p>
                      <DragDropContext onDragEnd={handleComponentDragEnd}>
                        <Droppable droppableId="productComponents">
                          {(provided) => (
                            <MDBListGroup
                              {...provided.droppableProps}
                              ref={provided.innerRef}
                            >
                              {products.filter(p => productData.components?.some(c => c.componentProductID === p.id))
                                .sort((a, b) => (productData.components.find(c => c.componentProductID === a.id)?.order || 0) - (productData.components.find(c => c.componentProductID === b.id)?.order || 0))
                                .map((product, index) => {
                                  const component = productData.components.find(c => c.componentProductID === product.id);
                                  return (
                                    <Draggable
                                      key={component.id}
                                      draggableId={component.id}
                                      index={index}
                                    >
                                      {(provided, snapshot) => (
                                        <MDBListGroupItem
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          style={{
                                            ...provided.draggableProps.style,
                                            backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'white'
                                          }}
                                        >
                                          <MDBRow className="align-items-center">
                                            <MDBCol size="1">
                                              <div {...provided.dragHandleProps} style={{ cursor: 'grab' }}>
                                                <MDBIcon fas icon="grip-vertical" />
                                              </div>
                                            </MDBCol>
                                            <MDBCol size="11" lg="3" style={{ minWidth: "7rem" }}>
                                              <MDBInput
                                                label={t("Quantity")}
                                                type="number"
                                                min={1}
                                                step={1}
                                                value={componentQtyDrafts[component.id] ?? component?.qty ?? ""}
                                                onChange={(e) => handleComponentQtyChange(component.id, e.target.value)}
                                                onBlur={() => handleComponentQtyBlur(component.id)}
                                              />
                                            </MDBCol>
                                            <MDBCol size="12" lg="5">
                                              {product.name?.find(n => n.language === i18n.language)?.text || product.name?.[0]?.text}
                                              <br />
                                              <span className="small text-muted">{componentMetaLine(product)}</span>
                                            </MDBCol>
                                            <MDBCol size="12" lg="3">
                                              <MDBBtn onClick={() => removeProductComponents(component.id)} color="danger" outline disabled={componentMutationRunning}>
                                                {t("Remove")}
                                              </MDBBtn>
                                            </MDBCol>
                                          </MDBRow>
                                        </MDBListGroupItem>
                                      )}
                                    </Draggable>
                                  );
                                })}
                              {provided.placeholder}
                            </MDBListGroup>
                          )}
                        </Droppable>
                      </DragDropContext>
                    </MDBCol>

                  </MDBRow>

                </MDBTabsPane>
                <MDBTabsPane open={activeTab === 'events'}>
                  <MDBRow>
                    <MDBCol size="12" lg="6">
                      <MDBTypography tag="h5">{t("Available Events")}</MDBTypography>
                      <MDBListGroup>
                        {events.filter(event => !productData.availableInEvents?.items.some(item => item.eventId === event.id)).map((event) => (
                          <MDBListGroupItem key={event.id}>
                            <MDBRow>
                              <MDBCol size="12" lg="9">
                                {event.name.find(n => n.language === i18n.language)?.text || event.name[0]?.text}
                              </MDBCol>
                              <MDBCol size="12" lg="3">
                                <MDBBtn onClick={() => addProductToEvent(event.id)} color="primary" outline>
                                  {t("Add")}
                                </MDBBtn>
                              </MDBCol>
                            </MDBRow>
                          </MDBListGroupItem>
                        ))}
                      </MDBListGroup>
                    </MDBCol>
                    <MDBCol size="12" lg="6">
                      <MDBTypography tag="h5">{t("Product Available In Events")}</MDBTypography>
                      <MDBListGroup style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {productData.eventProducts?.items.map((eventProduct) => (
                          <MDBListGroupItem key={eventProduct.id} className="d-flex justify-content-between">
                            <div style={{ width: "500px" }}>
                              <span>{eventProduct.event.name.find(n => n.language === i18n.language)?.text || eventProduct.event.name[0]?.text}</span>
                            </div>
                            <div className="d-flex align-items-center">
                              <MDBInput
                                type="number"
                                label={t("Price")}
                                value={eventProduct.price}
                                onChange={(e) =>
                                  handleEventProductUpdate(eventProduct.id, { price: parseFloat(e.target.value) })
                                }
                              >
                                <i className="fas fa-euro-sign trailing"></i>
                              </MDBInput>
                              <TimeRangePriceButton
                                hasTimeRangePrices={eventProduct.timeRangePrices?.length > 0}
                                onClick={() => {
                                  setSelectedEventProduct(eventProduct);
                                  setTimeRangePriceModalOpen(true);
                                }}
                              />
                              <MDBInput
                                type="number"
                                label={t("Stock")}
                                value={eventProduct.stock}
                                onChange={(e) =>
                                  handleEventProductUpdate(eventProduct.id, { stock: parseInt(e.target.value) })
                                }
                              />
                              <MDBBtn
                                onClick={() => removeProductFromEvent(eventProduct.id)}
                                color="danger"
                                className="ms-2"
                                style={{ width: '250px' }}
                                size="sm"
                                outline
                              >
                                {t("Remove")}
                              </MDBBtn>
                            </div>
                          </MDBListGroupItem>
                        ))}
                      </MDBListGroup>
                    </MDBCol>
                  </MDBRow>
                </MDBTabsPane>
                <MDBTabsPane open={activeTab === 'stock'}>
                  {/* Stock */}
                  <MDBInput
                    label={t("Stock")}
                    type="number"
                    value={productData.stock}
                    onChange={(e) => setProductData({ ...productData, stock: e.target.value })}
                  />
                </MDBTabsPane>
                <MDBTabsPane open={activeTab === 'orders'}>
                  {/* Orders */}
                </MDBTabsPane>
              </MDBTabsContent>





              {/* LECTURE SCRIPT */}
              {/* <MDBRow className="d-flex justify-content-evenly">
  <MDBCol size="12" lg="10" xl="8" xxl="7">
  <hr className="my-4" />
    <div className="mb-2 mb-lg-4">
      <MDBTypography tag="h5">
      {t("Optional: Upload a file for the product")}
      </MDBTypography>
    </div>
    {productData?.file?.fileName && (
      <p className="text-muted">{t("File uploaded successfully")}</p>
    )
    }
    <MDBFile id='fileDE' onChange={e => handleSetFile(e.target.files[0])} />
      
    <div className="mt-3">
      <MDBCheckbox
        name="script-condition"
        id="script-condition"
        label={t("I confirm that I have the rights to use the file and that it can be made public.")}
        checked={productData?.conditions?.findIndex(c => c.name === "file") > -1 ? productData?.conditions[productData?.conditions?.findIndex(c => c.name === "file")].accepted : false}
        onChange={(e) => {setProductData(oldData => ({...oldData, conditions: [{name: "file", accepted: e.target.checked}, ...oldData.conditions?.filter(c => c.name !== "file")]}))}}
      />
    </div>
    
  </MDBCol>
  
</MDBRow> */}
              {/* LECTURE SCRIPT END */}


            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      {/* <MDBModal open={selectTopicsModal} onClose={() => setSelectTopicsModal(false)} staticBackdrop>
  <MDBModalDialog centered size="xl">
      <MDBModalContent >
          <MDBModalHeader>
              <MDBModalTitle>Select Topics</MDBModalTitle>
              <MDBBtn className="btn-close" color="none" onClick={toggleSelectTopicsModal}></MDBBtn>
          </MDBModalHeader>
          <MDBModalBody>
              <SelectTopics
                topics={topics}
                topicGroups={topicGroups}
                isActive={selectTopicsModal}
                toggleActive={toggleSelectTopicsModal}
                selectedTopics={selectedTopics}
                setSelectedTopics={handleSetSelectedTopics}
                parentTopics={topics}
                parentTopicGroups={topicGroups}
                maxSelect={1}
                />
          </MDBModalBody>
          <MDBModalFooter>
              <MDBBtn color="secondary" onClick={toggleSelectTopicsModal}>{t("Close")}</MDBBtn>
          </MDBModalFooter>
      </MDBModalContent>
  </MDBModalDialog>
</MDBModal> */}
      <ConfirmModal
        open={Boolean(duplicateBooths)}
        title={t('Booth number already exists')}
        message={
          <div>
            <p>
              {t('Another booth product already uses the booth number {{boothNumber}}:', {
                boothNumber: cleanBoothNumberInput(productData.boothNumber) || '',
              })}
            </p>
            <ul>
              {(duplicateBooths || []).map((product) => (
                <li key={product.id}>
                  <Link to={`/admin/booth/${product.id}`} target="_blank" rel="noopener noreferrer">
                    {getLocalizedText(product.name) || product.id}
                  </Link>{' '}
                  ({product.sku})
                </li>
              ))}
            </ul>
            <p className="mb-0 small text-muted">
              {t('Booths are matched to the hall plan by booth number. Only one of them should be assigned to the same event.')}
            </p>
          </div>
        }
        confirmLabel={t('Save anyway')}
        onConfirm={saveProduct}
        onClose={() => setDuplicateBooths(null)}
        busy={isLoading}
      />
      {selectedEventProduct && (
        <TimeRangePriceModal
          isOpen={timeRangePriceModalOpen}
          onClose={() => {
            setTimeRangePriceModalOpen(false);
            setSelectedEventProduct(null);
          }}
          eventProduct={selectedEventProduct}
          onUpdateEventProduct={updateEventProduct}
        />
      )}
      {toast && (
        <MDBAlert
          key={toast.key}
          color={toast.color}
          autohide
          position="top-right"
          delay={toast.color === "danger" ? 6000 : 2500}
          appendToBody
          open={toast.open}
          onClose={() => setToast((current) => (current?.key === toast.key ? { ...current, open: false } : current))}
          onClosed={() => setToast((current) => (current?.key === toast.key ? null : current))}
        >
          {toast.message}
        </MDBAlert>
      )}
    </>
  );
};

export default CreateProductPage;
